import React, { useState } from 'react';
import { Check, Eye, EyeOff, KeyRound, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from './ui/dialog';

/**
 * Changement de mot de passe obligatoire, à la première connexion.
 *
 * Même facture que les fenêtres de la page de connexion (PopupConnexion,
 * AlerteConnexion) : primitives Dialog, en-tête à tuile, format large — les
 * champs à gauche, les règles à droite, cochées au fil de la saisie.
 *
 * Le changement est obligatoire : ni Échap, ni un clic à côté ne ferment la
 * fenêtre, comme dans la version précédente qui n'avait pas de bouton fermer.
 * Ancienne version : components/dump/PasswordChangeModal-V1-avant-refonte.jsx
 */
const REGLES = [
  { cle: 'longueur', libelle: '8 caractères au moins', test: (mdp) => mdp.length >= 8 },
  { cle: 'majuscule', libelle: 'Une lettre majuscule', test: (mdp) => /[A-Z]/.test(mdp) },
  { cle: 'minuscule', libelle: 'Une lettre minuscule', test: (mdp) => /[a-z]/.test(mdp) },
  { cle: 'chiffre', libelle: 'Un chiffre', test: (mdp) => /\d/.test(mdp) },
  { cle: 'special', libelle: 'Un caractère spécial (@ $ ! % * ? & …)', test: (mdp) => /[!@#$%^&*(),.?":{}|<>]/.test(mdp) }
];

const champ = 'h-11 w-full rounded-xl border border-[#d6e3ea] bg-white px-3.5 pr-11 text-sm text-gray-900 placeholder:text-gray-400 focus:border-[#246990] focus:outline-none focus:ring-2 focus:ring-[#246990]/20';

const Regle = ({ ok, children }) => (
  <li className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-700' : 'text-gray-500'}`}>
    <span className={`flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border ${
      ok ? 'border-emerald-600 bg-emerald-600 text-white' : 'border-gray-300 bg-white'
    }`}>
      {ok && <Check size={12} strokeWidth={3} />}
    </span>
    {children}
  </li>
);

const PasswordChangeModal = ({ onSubmit }) => {
  const [nouveau, setNouveau] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [afficher, setAfficher] = useState(false);
  const [erreur, setErreur] = useState('');
  const [enCours, setEnCours] = useState(false);

  const resultats = REGLES.map((r) => ({ ...r, ok: r.test(nouveau) }));
  const identiques = Boolean(nouveau) && nouveau === confirmation;
  const valide = resultats.every((r) => r.ok) && identiques;

  const soumettre = async (e) => {
    e.preventDefault();
    setErreur('');
    if (!valide) {
      setErreur('Le mot de passe ne respecte pas encore toutes les règles.');
      return;
    }
    setEnCours(true);
    try {
      await onSubmit(nouveau);
    } catch (err) {
      setErreur(err.message || 'Une erreur est survenue.');
    } finally {
      setEnCours(false);
    }
  };

  const bloquer = (e) => e.preventDefault();

  return (
    <Dialog open>
      <DialogContent
        className="w-[95vw] max-w-3xl !max-h-[92vh] overflow-hidden flex flex-col p-0 gap-0"
        onEscapeKeyDown={bloquer}
        onPointerDownOutside={bloquer}
        onInteractOutside={bloquer}
      >
        <form onSubmit={soumettre} className="flex min-h-0 flex-1 flex-col">
          <DialogHeader className="flex-row items-center gap-3 space-y-0 border-b px-5 py-4 text-left">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-[#246990] text-white">
              <KeyRound size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle className="text-lg font-semibold text-gray-900">Choisissez votre mot de passe</DialogTitle>
              <DialogDescription className="text-sm text-gray-500">
                Première connexion : pour votre sécurité, remplacez le mot de passe qui vous a été remis.
              </DialogDescription>
            </div>
          </DialogHeader>

          <div className="grid flex-1 gap-6 overflow-y-auto px-5 py-5 md:grid-cols-[minmax(0,1fr)_260px]">
            {/* Saisie */}
            <div className="space-y-4">
              <div>
                <label htmlFor="nouveau-mot-de-passe" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <input
                    id="nouveau-mot-de-passe"
                    type={afficher ? 'text' : 'password'}
                    value={nouveau}
                    onChange={(e) => setNouveau(e.target.value)}
                    className={champ}
                    placeholder="Votre nouveau mot de passe"
                    autoComplete="new-password"
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => setAfficher((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    aria-label={afficher ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    {afficher ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>

              <div>
                <label htmlFor="confirmation-mot-de-passe" className="mb-1.5 block text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Confirmation
                </label>
                <input
                  id="confirmation-mot-de-passe"
                  type={afficher ? 'text' : 'password'}
                  value={confirmation}
                  onChange={(e) => setConfirmation(e.target.value)}
                  className={champ}
                  placeholder="Saisissez-le une seconde fois"
                  autoComplete="new-password"
                />
                {confirmation && !identiques && (
                  <p className="mt-1.5 text-xs text-amber-700">Les deux saisies ne sont pas identiques.</p>
                )}
              </div>

              {erreur && (
                <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{erreur}</p>
              )}
            </div>

            {/* Règles */}
            <div className="rounded-xl border border-[#dbe7ee] bg-[#f4f9fb] p-4">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Votre mot de passe contient</p>
              <ul className="space-y-2.5">
                {resultats.map((r) => <Regle key={r.cle} ok={r.ok}>{r.libelle}</Regle>)}
                <Regle ok={identiques}>Deux saisies identiques</Regle>
              </ul>
            </div>
          </div>

          <DialogFooter className="border-t bg-gray-50 px-5 py-4">
            <Button
              type="submit"
              disabled={enCours || !valide}
              className="h-9 min-w-[220px] justify-center gap-2 border border-transparent bg-[#246990] text-white hover:bg-[#1f5a7a] disabled:opacity-50"
            >
              {enCours && <Loader2 size={15} className="animate-spin" />}
              {enCours ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default PasswordChangeModal;
